import { create } from 'zustand';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  sidebarOpen: boolean;
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  language: string;
  toggleSidebar: () => void;
  setTheme: (theme: Theme) => void;
  setLanguage: (lang: string) => void;
  initTheme: () => void;
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return getSystemTheme();
  return theme;
}

function applyThemeToDOM(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

const savedTheme = (typeof localStorage !== 'undefined'
  ? localStorage.getItem('tropayment_theme') as Theme
  : null) || 'light';

const initialResolved = resolveTheme(savedTheme);
applyThemeToDOM(initialResolved);

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: true,
  theme: savedTheme,
  resolvedTheme: initialResolved,
  language: 'en',

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setTheme: (theme) => {
    const resolved = resolveTheme(theme);
    localStorage.setItem('tropayment_theme', theme);
    applyThemeToDOM(resolved);
    set({ theme, resolvedTheme: resolved });
  },

  setLanguage: (language) => set({ language }),

  initTheme: () => {
    const stored = localStorage.getItem('tropayment_theme') as Theme || 'light';
    const resolved = resolveTheme(stored);
    applyThemeToDOM(resolved);
    set({ theme: stored, resolvedTheme: resolved });

    // Listen for system theme changes
    if (typeof window !== 'undefined') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        const currentTheme = useUIStore.getState().theme;
        if (currentTheme === 'system') {
          const newResolved = getSystemTheme();
          applyThemeToDOM(newResolved);
          set({ resolvedTheme: newResolved });
        }
      });
    }
  },
}));
